import React from "react";
import { Box } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { toast } from "react-toastify";
import { COLORS } from "../../assets/theme";
import CustomTable from "../../components/table/CustomTable";
import SearchHeaderContainer from "../../components/table/SearchHeaderContainer";
import Actions from "../../components/Actions";
import GenericFormDialog, { Field } from "../../components/GenericFormDialog";
import ConfirmDialog from "../../components/ConfirmDialog";
import CustomLabel from "../../components/CustomLabel";
import { useLocation, useParams } from "react-router-dom";
import { getAllBranches } from "../../services/BranchesService";
import {
  createService,
  deleteService,
  getAllServices,
  updateService,
} from "../../services/ServicesService";
import { getAllCurrencys } from "../../services/CurrencyService";
import { getAllUnits } from "../../services/UnitService";

const Services: React.FC = () => {
  const location = useLocation();
  const params = useParams();
  const venueId = params.branchId;

  console.log("Reports component - params:", params);
  console.log("Reports component - venueId from params:", venueId);

  const [branches, setBranches] = React.useState<any[]>([]);
  const [currencies, setCurrencies] = React.useState<any[]>([]);
  const [units, setUnits] = React.useState<any[]>([]);
  const [selectValue, setSelectValue] = React.useState<
    string | number | (string | number)[]
  >("");
  const [selectCleared, setSelectCleared] = React.useState(false);
  const [editOpen, setEditOpen] = React.useState(false);
  const [editingRow, setEditingRow] = React.useState<any | null>(null);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [toDeleteId, setToDeleteId] = React.useState<string | number | null>(
    null
  );

  const [search, setSearch] = React.useState<string>("");
  const [loading, setLoading] = React.useState<boolean>(false);
  const [refetchTrigger, setRefetchTrigger] = React.useState(0);
  const [addInitialValues, setAddInitialValues] = React.useState<
    Record<string, any>
  >({
    price_discount: "0.00",
    order: "",
    currency_id: "",
    unit_id: "",
    active: true,
  });

  const formFields: Field[] = [
    {
      name: "branch_id",
      label: "Branch",
      type: "dropdown",
      options: branches.map((b: any) => ({
        label: b.name || b.business_name || "Unknown Branch",
        value: String(b.business_id),
      })),
      searchable: true,
      required: true,
    },
    { name: "title", label: "Service Name" },
    { name: "price", label: "Price" },
    {
      name: "currency_id",
      label: "Currency",
      type: "dropdown",
      options: currencies.map((c: any) => ({
        label: c.currency_label || c.label || "",
        value: c.id,
      })),
    },
    {
      name: "unit_id",
      label: "Unit",
      type: "dropdown",
      options: units.map((u: any) => ({
        label: u.label || "",
        value: u.id,
      })),
    },
    { name: "order", label: "Order" },
  ];

  const openEditForId = (id: string | number) => {
    const found = rows.find((r: any) => String(r.id) === String(id));
    if (found) {
      setEditingRow(found);
      setEditOpen(true);
    }
  };
  const wiredActions = Actions({
    handleOpenEditModal: { handler: openEditForId },
    onDelete: {
      handler: async (id: number | string) => {
        setToDeleteId(id);
        setConfirmOpen(true);
      },
    },
  });

  const [rows, setRows] = React.useState<any[]>([]);
  const getErrorMessage = (error: any, fallback: string) => {
    return (
      error?.response?.data?.message ||
      error?.response?.data?.msg ||
      error?.response?.data?.error ||
      error?.message ||
      fallback
    );
  };
  React.useEffect(() => {
    if (!editingRow) {
      const defaultCurrency =
        currencies && currencies.length > 0
          ? currencies[0].id ?? currencies[0]._id ?? ""
          : "";
      const defaultUnit = units && units.length > 0 ? units[0].id ?? "" : "";
      const defaultBranch =
        selectValue !== "" &&
        selectValue !== null &&
        selectValue !== undefined &&
        (!Array.isArray(selectValue) || selectValue.length > 0)
          ? String(selectValue)
          : branches && branches.length > 0
          ? branches[0].business_id ?? branches[0].businessId ?? ""
          : "";
      setAddInitialValues((prev) => ({
        ...prev,
        currency_id: defaultCurrency,
        unit_id: defaultUnit,
        branch_id: defaultBranch,
      }));
    }
  }, [currencies, units, editingRow, branches, selectValue]);

  React.useEffect(() => {
    const fetchBranches = async () => {
      console.log("fetchBranches called with venueId:", venueId);
      try {
        if (!venueId) {
          console.error("No venue ID available, venueId:", venueId);
          setBranches([]);
          return;
        }

        console.log("Calling getAllBranches with venueId:", venueId);
        const response = await getAllBranches(venueId);
        console.log("Branches API response:", response);

        let branchesData = [];
        if (response && response.businesses) {
          branchesData = response.businesses;
        } else if (response && response.data) {
          branchesData = response.data;
        } else if (Array.isArray(response)) {
          branchesData = response;
        }

        console.log("Processed branches data:", branchesData);
        const processedBranches = Array.isArray(branchesData)
          ? branchesData
          : [];
        setBranches(processedBranches);
        if (processedBranches.length > 0 && !selectValue && !selectCleared) {
          setSelectValue(processedBranches[0].business_id);
        }
      } catch (error) {
        console.error("Error fetching branches:", error);
        toast.error(getErrorMessage(error, "Failed to fetch branches."), {
          position: "top-right",
        });
        setBranches([]);
      }
    };

    if (venueId) {
      fetchBranches();
    } else {
      console.log("No venueId available, skipping branch fetch");
    }
  }, [venueId]);

  React.useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const businessId =
          selectValue && String(selectValue)
            ? String(selectValue)
            : !selectCleared && venueId
            ? String(venueId)
            : undefined;
        if (!businessId) return;
        const response = await getAllCurrencys(businessId);
        console.log("Currencies fetched:", response);

        let currenciesData: any[] = [];
        if (response && Array.isArray(response)) {
          currenciesData = response;
        } else if (response && response.data) {
          currenciesData = Array.isArray(response.data) ? response.data : [];
        } else if (response && response.currencies) {
          currenciesData = response.currencies;
        }

        setCurrencies(currenciesData);
      } catch (error) {
        console.error("Error fetching currencies:", error);
        setCurrencies([]);
      }
    };

    fetchCurrencies();
  }, [selectValue, venueId]);

  React.useEffect(() => {
    const fetchUnits = async () => {
      try {
        const businessId =
          selectValue && String(selectValue)
            ? String(selectValue)
            : !selectCleared && venueId
            ? String(venueId)
            : undefined;
        if (!businessId) return;
        const response = await getAllUnits(businessId);
        console.log("Units fetched:", response);

        let unitsData: any[] = [];
        if (response && Array.isArray(response)) {
          unitsData = response;
        } else if (response && response.data) {
          unitsData = Array.isArray(response.data) ? response.data : [];
        } else if (response && response.units) {
          unitsData = response.units;
        }

        const mapped = unitsData.map((u: any) => ({
          id: u.id ?? u._id ?? u.unit_id,
          label: u.per || u.label || "",
        }));

        setUnits(mapped);
      } catch (error) {
        console.error("Error fetching units:", error);
        setUnits([]);
      }
    };

    fetchUnits();
  }, [selectValue, venueId]);

  React.useEffect(() => {
    const fetchSpecialities = async () => {
      setLoading(true);
      try {
        const businessId =
          selectValue && String(selectValue)
            ? String(selectValue)
            : !selectCleared && venueId
            ? String(venueId)
            : undefined;
        const response = await getAllServices(businessId);
        console.log("Services fetched:", response);

        let specialitiesData: any[] = [];
        if (response && Array.isArray(response)) {
          specialitiesData = response;
        } else if (response && response.data) {
          specialitiesData = Array.isArray(response.data)
            ? response.data
            : response.data.services || [];
        } else if (response && response.services) {
          specialitiesData = response.services;
        }

        const mapped = (specialitiesData || []).map((s: any) => {
          return {
            id: s.id ?? s._id,
            title: s.title ?? "",
            price: s.price ?? "",
            price_discount: String(s.price_discount ?? s.discount ?? "0.00"),
            active: s.active ?? true,
            currency: s.currency_label ?? "",
            currency_id: s.currency_id ?? "",
            unit: s.per ?? s.unit_id ?? "",
            unit_id: s.unit_id ?? "",
            order: s.order ?? "",
            business_id:
              s.business_id ?? s.businessId ?? s.branch_id ?? s.branchId ?? "",
          };
        });

        setRows(mapped);
      } catch (error: any) {
        console.error("Error fetching services:", error);
        toast.error(getErrorMessage(error, "Failed to fetch services."), {
          position: "top-right",
        });
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSpecialities();
  }, [refetchTrigger, selectValue, venueId]);

  return (
    <Box sx={{ width: "100%", maxWidth: "90vw", overflowX: "hidden" }}>
      <Box sx={{ mt: { xs: 2, sm: 3 } }}>
        <CustomLabel
          text="Services"
          variant="h4"
          fontWeight="semi-bold"
          sx={{ mb: 2 }}
        />
        <SearchHeaderContainer
          search={search}
          setSearch={setSearch}
          selectConfig={{
            options: branches.map((branch) => ({
              label: branch.name || "Unknown Branch",
              value: branch.business_id,
            })),
            value: selectValue,
            onChange: (v: any) => {
              const isEmpty =
                v === "" || v === null || (Array.isArray(v) && v.length === 0);
              if (isEmpty) {
                setSelectValue("");
                setSelectCleared(true);
                return;
              }

              setSelectCleared(false);
              setSelectValue(v);
            },
            label: "Select Branch",

            placeholder: "Select branch",
            size: "medium",
            searchable: false,
            clearable: true,
            multiSelect: false,
            sx: {
              backgroundColor: COLORS.WhiteBackground,
            },
          }}
          buttonConfig={[
            {
              size: "small",
              bgColor: COLORS.MainColor,
              startIcon: <AddIcon />,
              onClick: () => {
                setEditingRow(null);
                setEditOpen(true);
              },
              textColor: COLORS.WhiteText,
              minHeight: 36,
              maxHeight: 36,
              sx: {
                width: { xs: "96%", sm: 36 },
                minWidth: { sm: 36 },
                marginRight: { xs: 1.5, sm: 0 },
              },
            },
          ]}
        />
        <CustomTable<any>
          filters={{ page: 0, size: 10, search }}
          clientSidePagination={true}
          loading={loading}
          fields={[
            { key: "title", header: "Title" },
            { key: "price", header: "Price" },
            { key: "currency", header: "Currency" },
            { key: "unit", header: "Unit" },
            { key: "order", header: "Order" },
          ]}
          actions={wiredActions}
          fetchFunc={async (params?: any) => {
            const q = (params && params.search) || search || "";

            const normalizedQuery = String(q || "")
              .toLowerCase()
              .trim();

            const filtered = rows.filter((r: any) => {
              if (!normalizedQuery) return true;

              const haystack = [
                r.title,
                r.price,
                r.price_discount,
                r.currency,
                r.unit,
                r.order,
              ]
                .filter(Boolean)
                .map((v: any) => String(v).toLowerCase())
                .join(" ");

              return haystack.includes(normalizedQuery);
            });

            const content = filtered;
            const totalElements = content.length;
            const pageSize = (params && params.size) || 10;
            const totalPages = Math.max(1, Math.ceil(totalElements / pageSize));

            return {
              content,
              number: 0,
              size: pageSize,
              totalElements,
              totalPages,
            };
          }}
        />
        <GenericFormDialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          title={editingRow ? `Edit Service` : "Add Service"}
          fields={editingRow ? formFields : [...formFields]}
          initialValues={
            editingRow
              ? {
                  id: editingRow.id ?? "",
                  title: editingRow.title || "",
                  price: editingRow.price || "",
                  price_discount: editingRow.price_discount ?? "0.00",
                  active: editingRow.active ?? true,
                  currency_id: editingRow.currency_id || "",
                  unit_id: editingRow.unit_id || "",
                  branch_id:
                    editingRow.business_id ?? editingRow.branch_id ?? String(selectValue) ?? venueId ?? "",
                  order: editingRow.order || "",
                }
              : {
                  title: "",
                  price: "",
                  ...addInitialValues,
                  branch_id:
                    selectValue !== "" &&
                    selectValue !== null &&
                    selectValue !== undefined
                      ? String(selectValue)
                      : addInitialValues.branch_id
                      ? String(addInitialValues.branch_id)
                      : venueId
                      ? String(venueId)
                      : "",
                }
          }
          onSubmit={async (values) => {
            try {
                  // Prefer branch selection from the form (values.branch_id) then fallback to dropdown searchValue or venueId
                  const businessId = values.branch_id
                    ? String(values.branch_id)
                    : selectValue
                    ? String(selectValue)
                    : venueId;

              if (editingRow) {
                await updateService(String(editingRow.id), {
                  id: editingRow.id,
                  title: values.title,
                  price: values.price,
                  price_discount: values.price_discount ?? "0.00",
                  currency_id: values.currency_id,
                  unit_id: values.unit_id,
                  order: values.order,
                  business_id: businessId,
                });
                toast.success("Service updated successfully!", {
                  position: "top-right",
                });
                setRefetchTrigger((prev) => prev + 1);
              } else {
                await createService({
                  title: values.title,
                  price: values.price,
                  price_discount: values.price_discount ?? "0.00",
                  currency_id: values.currency_id,
                  unit_id: values.unit_id,
                  order: values.order,
                  business_id: businessId,
                });
                toast.success("Service created successfully!", {
                  position: "top-right",
                });
                setRefetchTrigger((prev) => prev + 1);
              }
              setEditOpen(false);
              setEditingRow(null);
            } catch (error: any) {
              console.error("Error saving service:", error);
              const errorMessage = getErrorMessage(
                error,
                "Failed to save service. Please try again."
              );
              toast.error(errorMessage, { position: "top-right" });
            }
          }}
        />
        <ConfirmDialog
          open={confirmOpen}
          onClose={() => {
            setConfirmOpen(false);
            setToDeleteId(null);
          }}
          title="Delete entry"
          message="Are you sure you want to delete this entry? This action cannot be undone."
          onConfirm={async () => {
            if (toDeleteId === null) return;
            try {
              await deleteService(String(toDeleteId));
              toast.success("Service deleted successfully!", {
                position: "top-right",
              });
              setRefetchTrigger((prev) => prev + 1);
            } catch (error: any) {
              console.error("Error deleting service:", error);
              const errorMessage = getErrorMessage(
                error,
                "Failed to delete service. Please try again."
              );
              toast.error(errorMessage, { position: "top-right" });
            } finally {
              setConfirmOpen(false);
              setToDeleteId(null);
            }
          }}
        />
      </Box>
    </Box>
  );
};

export default Services;
